import clsx from "clsx";
import { ChevronsUpDownIcon } from "lucide-react";
import type {
	FieldErrors,
	FieldValues,
	Path,
	RegisterOptions,
	UseFormRegister,
} from "react-hook-form";

import { FORM_SELECT_CHEVRON_CLASS_NAME } from "~/lib/profession/formSelectChevronClassName";

interface FormSelectProps {
	description?: string;
	label: Path<FieldValues>;
	name: string;
	options?: {
		label: string;
		value: any;
		disabled?: boolean;
		default?: boolean;
	}[];
	disabled?: boolean;
	register: UseFormRegister<any>;
	errors: FieldErrors<any>;
	requiredOptions?: RegisterOptions<any, string> | undefined;
	onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
	labelClassName?: string;
	isOptional?: boolean;
	placeholder?: string;
	isEmpty?: boolean;
}

const FormSelect = ({
	label,
	name,
	options,
	register,
	errors,
	requiredOptions,
	onChange,
	disabled,
	isOptional,
	labelClassName,
	placeholder,
	isEmpty,
	description,
}: FormSelectProps) => {
	return (
		<div className="flex flex-1 flex-col gap-2">
			<label
				htmlFor={name}
				className={clsx(
					"flex items-center justify-between font-medium text-gray-700 dark:text-slate-50",
					labelClassName,
				)}
			>
				{label}{" "}
				{isOptional ? (
					<span className="font-normal text-slate-500 dark:text-slate-300">
						Optional
					</span>
				) : (
					<span className="font-normal text-red-500 dark:text-red-300">
						{errors[name]?.message as string}
					</span>
				)}
			</label>

			{description && (
				<p className="-mt-1 text-slate-500 text-sm dark:text-slate-300">
					{description}
				</p>
			)}

			<div className="relative">
				<select
					disabled={disabled}
					{...register(name, {
						onChange,
						...requiredOptions,
					})}
					className={clsx(
						`w-full cursor-pointer appearance-none rounded-lg border px-3 py-2 shadow shadow-slate-100 dark:bg-slate-700 dark:shadow-slate-900`,
						{
							"border-red-400": errors[name],
							"text-slate-500": isEmpty,
						},
					)}
					name={name}
					id={name}
					aria-invalid={errors[name] ? "true" : "false"}
				>
					<option value={""} disabled={!isOptional}>
						{isOptional && !isEmpty
							? `Clear Selection`
							: (placeholder ?? `Select a ${label}`)}
					</option>

					{options?.map((option, index) => (
						<option key={index} disabled={option.disabled} value={option.value}>
							{option.label}
						</option>
					))}
				</select>

				<ChevronsUpDownIcon
					size={17}
					className={FORM_SELECT_CHEVRON_CLASS_NAME}
					aria-hidden
				/>
			</div>
		</div>
	);
};

export default FormSelect;
